import React, { useState, useEffect, useRef } from "react";
import "./FreightMapComponent.css";

const libraries = ["places", "directions"];
const containerStyle = { width: "100%", height: "100%" };

const FreightMapComponent = ({ trips = [], setTripDuration }) => {
    const [directions, setDirections] = useState([]);
    const [activeTrip, setActiveTrip] = useState(null);
    const [map, setMap] = useState(null);
    const [isLoaded, setIsLoaded] = useState(false);
    const [loadError, setLoadError] = useState(null);
    const [mapStabilized, setMapStabilized] = useState(false);
    const [routesCalculated, setRoutesCalculated] = useState(false);
    const prevTripsCountRef = useRef(0);
    const [center] = useState({ lat: 18.54, lng: 73.85 }); // India's center

    const driverMarkersRef = useRef({});
    const [driverLocations, setDriverLocations] = useState({});
    const [lastUpdated, setLastUpdated] = useState(null);

    // Color palette for different routes
    const routeColors = [
        "#FF5252", "#4CAF50", "#2196F3", "#FF9800",
        "#9C27B0", "#00BCD4", "#FFEB3B", "#795548"
    ];

    // Helper function to load Google Maps API
    const loadGoogleMapsAPI = (apiKey) => {
        return new Promise((resolve, reject) => {
            // Check if already loaded
            if (window.google && window.google.maps) {
                resolve(window.google);
                return;
            }

            // Create callback function for when API loads
            window.initMap = () => {
                delete window.initMap;
                resolve(window.google);
            };

            // Create script element
            const script = document.createElement("script");
            script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&callback=initMap&libraries=${libraries.join(",")}`;
            script.async = true;
            script.defer = true;
            script.onerror = (error) => {
                console.error("Google Maps API failed to load:", error);
                reject(new Error("Google Maps API failed to load"));
            };

            // Add script to page
            document.head.appendChild(script);
        });
    };

    // Load Google Maps API on component mount
    useEffect(() => {
        // Get API key from environment or use a placeholder for development
        const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

        if (!apiKey) {
            setLoadError("Missing Google Maps API key");
            return;
        }

        loadGoogleMapsAPI(apiKey)
            .then(() => {
                console.log("Google Maps API loaded successfully");
                setIsLoaded(true);
            })
            .catch((error) => {
                console.error("Error loading Google Maps API:", error);
                setLoadError(error.message);
            });

        // Cleanup
        return () => {
            if (window.google && window.google.maps) {
                // Clean up any map instances if needed
            }
        };
    }, []);

    // Initialize map when API is loaded
    useEffect(() => {
        if (isLoaded && !map && window.google) {
            console.log("Initializing map...");

            const mapInstance = new window.google.maps.Map(
                document.getElementById("google-map-container"),
                {
                    center,
                    zoom: 5,
                    minZoom: 3,
                    maxZoom: 16,
                    streetViewControl: false,
                    mapTypeControl: false,
                    fullscreenControl: false,
                    zoomControl: true,
                    mapTypeId: window.google.maps.MapTypeId.ROADMAP,
                    gestureHandling: 'cooperative',
                    disableDefaultUI: false,
                    styles: [
                        {
                            featureType: "poi",
                            elementType: "labels",
                            stylers: [{ visibility: "off" }]
                        }
                    ]
                }
            );

            let mapReadyTimer;
            const idleListener = mapInstance.addListener('idle', () => {
                if (mapReadyTimer) {
                    clearTimeout(mapReadyTimer);
                }

                mapReadyTimer = setTimeout(() => {
                    if (!mapStabilized) {
                        setMapStabilized(true);
                    }
                }, 1000);
            });

            mapInstance.idleListener = idleListener;
            setMap(mapInstance);
        }

        return () => {
            if (map && map.idleListener) {
                window.google.maps.event.removeListener(map.idleListener);
            }
        };
    }, [isLoaded, map, center, mapStabilized]);

    // Function to fetch real-time locations
    const fetchDriverLocations = async () => {
        try {
            const freightIds = trips
                .filter(trip => trip.DriverID) // Assuming you have DriverID in trip data
                .map(trip => trip.DriverID)
                .join(',');

            if (!freightIds) return;

            const response = await fetch(`/freight-locations?freightIds=${freightIds}`);
            const data = await response.json();

            if (data.success) {
                const locationsMap = {};
                data.data.forEach(loc => {
                    locationsMap[loc._id] = {
                        lat: loc.latitude,
                        lng: loc.longitude,
                        updated: new Date(loc.timestamp)
                    };
                });
                setDriverLocations(locationsMap);
                setLastUpdated(new Date());
            }
        } catch (error) {
            console.error("Error fetching driver locations:", error);
        }
    };

    // Add to your useEffect that depends on trips
    useEffect(() => {
        if (trips.length > 0) {
            fetchDriverLocations();
        }
    }, [trips]);


    // Calculate routes for all trips
    useEffect(() => {
        const calculateRoutes = async () => {
            if (!window.google || !trips.length || !map) return;

            if (routesCalculated && prevTripsCountRef.current === trips.length) {
                return;
            }

            prevTripsCountRef.current = trips.length;
            const directionsService = new window.google.maps.DirectionsService();
            const newBounds = new window.google.maps.LatLngBounds();
            const directionsResults = [];

            setRoutesCalculated(false);

            for (const trip of trips) {
                if (!trip.DestinationFrom || !trip.DestinationTo) continue;

                try {
                    const result = await new Promise((resolve) => {
                        directionsService.route(
                            {
                                origin: trip.DestinationFrom,
                                destination: trip.DestinationTo,
                                travelMode: window.google.maps.TravelMode.DRIVING
                            },
                            (result, status) => {
                                if (status === "OK") {
                                    result.routes[0].legs[0].steps.forEach(step => {
                                        step.lat_lngs.forEach(latLng => {
                                            newBounds.extend(latLng);
                                        });
                                    });

                                    if (setTripDuration) {
                                        setTripDuration(prevState => ({
                                            ...prevState,
                                            [trip.TripID]: result.routes[0].legs[0].duration.text
                                        }));
                                    }

                                    resolve({
                                        tripId: trip.TripID,
                                        DriverName: trip.DriverName,
                                        from: trip.DestinationFrom,
                                        to: trip.DestinationTo,
                                        directions: result,
                                        username: trip.Username,
                                        profile: trip.Profile,
                                        truckNumber: trip.TruckNumber,
                                        status: trip.TransactionStatus
                                    });
                                } else {
                                    console.error("Direction service failed with status:", status);
                                    resolve(null);
                                }
                            }
                        );
                    });

                    if (result) {
                        directionsResults.push(result);
                    }
                } catch (error) {
                    console.error("Error calculating route:", error);
                }
            }

            if (directionsResults.length === 0) {
                setRoutesCalculated(true);
                return;
            }

            directions.forEach(dir => {
                if (dir.renderer) {
                    dir.renderer.setMap(null);
                }
                if (dir.startMarker) {
                    dir.startMarker.setMap(null);
                }
                if (dir.endMarker) {
                    dir.endMarker.setMap(null);
                }
            });

            const newDirections = directionsResults.map((route, index) => {
                const renderer = new window.google.maps.DirectionsRenderer({
                    directions: route.directions,
                    map: map,
                    polylineOptions: {
                        strokeColor: routeColors[index % routeColors.length],
                        strokeWeight: 4,
                        strokeOpacity: 0.8
                    },
                    suppressMarkers: true
                });

                // Add hover listeners to the polyline
                const polyline = renderer.getDirections().routes[0].overview_path;
                const routePolyline = new window.google.maps.Polyline({
                    path: polyline,
                    strokeColor: routeColors[index % routeColors.length],
                    strokeWeight: 8, // Make it slightly thicker for better hover
                    strokeOpacity: 0,
                    zIndex: 1
                });

                routePolyline.setMap(map);

                // Create info window (but don't show it yet)
                const contentString =
                    `<div class="trip-info">
                        <h4>Trip ID: ${route.tripId}</h4>
                        <p><strong>Truck:</strong> ${route.truckNumber}</p>
                        <p><strong>Status:</strong> ${route.status}</p>
                        <p><strong>From:</strong> ${route.from}</p>
                        <p><strong>To:</strong> ${route.to}</p>
                        <p><strong>Driver:</strong> ${route.DriverName}</p>
                        <p><strong>Duration:</strong> ${route.directions.routes[0].legs[0].duration.text}</p>
                        <p><strong>Distance:</strong> ${route.directions.routes[0].legs[0].distance.text}</p>
                    </div>`;

                const infoWindow = new window.google.maps.InfoWindow({
                    content: contentString,
                    disableAutoPan: true
                });

                // Add hover events
                routePolyline.addListener('mouseover', (event) => {
                    infoWindow.setPosition(event.latLng);
                    infoWindow.open(map);
                    // Highlight the route
                    renderer.setOptions({
                        polylineOptions: {
                            strokeColor: routeColors[index % routeColors.length],
                            strokeWeight: 6,
                            strokeOpacity: 1
                        }
                    });
                });

                routePolyline.addListener('mouseout', () => {
                    infoWindow.close();
                    // Restore original route style
                    renderer.setOptions({
                        polylineOptions: {
                            strokeColor: routeColors[index % routeColors.length],
                            strokeWeight: 4,
                            strokeOpacity: 0.8
                        }
                    });
                });

                const startMarker = new window.google.maps.Marker({
                    position: route.directions.routes[0].legs[0].start_location,
                    map: map,
                    icon: {
                        path: window.google.maps.SymbolPath.CIRCLE,
                        scale: 8,
                        fillColor: routeColors[index % routeColors.length],
                        fillOpacity: 1,
                        strokeWeight: 2,
                        strokeColor: "white"
                    },
                    label: {
                        text: "S",
                        color: "white",
                        fontSize: "12px",
                        fontWeight: "bold",
                    }
                });

                const endMarker = new window.google.maps.Marker({
                    position: route.directions.routes[0].legs[0].end_location,
                    map: map,
                    icon: {
                        path: window.google.maps.SymbolPath.CIRCLE,
                        scale: 8,
                        fillColor: routeColors[index % routeColors.length],
                        fillOpacity: 1,
                        strokeWeight: 2,
                        strokeColor: "white"
                    },
                    label: {
                        text: "D",
                        color: "white",
                        fontSize: "12px",
                        fontWeight: "bold"
                    }
                });

                return {
                    ...route,
                    renderer,
                    startMarker,
                    endMarker,
                    routePolyline,
                    infoWindow
                };
            });

            setDirections(newDirections);
            setRoutesCalculated(true);

            if (!mapStabilized && newDirections.length > 0) {
                const padding = {
                    top: 50,
                    right: 50,
                    bottom: 50,
                    left: 50
                };

                map.fitBounds(newBounds, padding);

                const listener = map.addListener('idle', () => {
                    if (map.getZoom() > 11) {
                        map.setZoom(11);
                    }
                    setMapStabilized(true);
                    window.google.maps.event.removeListener(listener);
                });
            }
        };

        if (isLoaded && map && trips.length) {
            calculateRoutes();
        }
    }, [isLoaded, map, trips.length, routesCalculated, mapStabilized]);

    // Handle active trip info window
    useEffect(() => {
        if (!map || !activeTrip) return;

        if (window.currentInfoWindow) {
            window.currentInfoWindow.close();
        }

        const contentString = `
            <div class="trip-info">
              <h4>Trip ID: ${activeTrip.tripId}</h4>
              <p><strong>Truck:</strong> ${activeTrip.truckNumber}</p>
              <p><strong>Status:</strong> ${activeTrip.status}</p>
              <p><strong>From:</strong> ${activeTrip.from}</p>
              <p><strong>To:</strong> ${activeTrip.to}</p>
              <p><strong>Driver:</strong> ${activeTrip.DriverName}</p>
              <p><strong>Duration:</strong> ${activeTrip.directions.routes[0].legs[0].duration.text}</p>
              <p><strong>Distance:</strong> ${activeTrip.directions.routes[0].legs[0].distance.text}</p>
            </div>
          `;

        const infoWindow = new window.google.maps.InfoWindow({
            content: contentString,
            position: activeTrip.directions.routes[0].legs[0].start_location
        });

        infoWindow.open(map);
        window.currentInfoWindow = infoWindow;

        infoWindow.addListener("closeclick", () => {
            setActiveTrip(null);
        });

        return () => {
            if (window.currentInfoWindow) {
                window.currentInfoWindow.close();
            }
        };
    }, [activeTrip, map]);

    //real-time driver locations
    useEffect(() => {
        if (!map || !window.google || !driverLocations) return;

        // Clear existing markers
        Object.values(driverMarkersRef.current).forEach(marker => {
            if (marker && marker.setMap) {
                marker.setMap(null);
            }
        });
        driverMarkersRef.current = {};

        // Add new markers
        Object.entries(driverLocations).forEach(([driverId, location]) => {

            if (isNaN(location.lat) || isNaN(location.lng)) {
                console.warn(`Invalid location for driver ${driverId}`);
                return;
            }

            const marker = new window.google.maps.Marker({
                position: { lat: location.lat, lng: location.lng },
                map: map,
                icon: {
                    path: window.google.maps.SymbolPath.CIRCLE,
                    scale: 8,
                    fillColor: "#4285F4",
                    fillOpacity: 1,
                    strokeWeight: 2,
                    strokeColor: "white"
                },
                title: `Driver ${driverId} - Last updated: ${location.updated.toLocaleTimeString()}`
            });

            driverMarkersRef.current[driverId] = marker;
        });
    }, [driverLocations, map]);

    //real-time driver locations
    useEffect(() => {
        return () => {
            // Clean up markers on unmount
            Object.values(driverMarkersRef.current).forEach(marker => {
                if (marker && marker.setMap) {
                    marker.setMap(null);
                }
            });
        };
    }, []);


    if (loadError) {
        return (
            <div className="loading-map error">
                <p>Failed to load Google Maps</p>
                <p className="error-details">{loadError}</p>
                <p>Check your API key in the environment variables</p>
            </div>
        );
    }

    if (!isLoaded) {
        return <div className="loading-map">Loading Google Maps API...</div>;
    }

    return (
        <div className="map-container">
            {/* real-time driver locations */}
            <div className="map-controls">
                <button
                    onClick={fetchDriverLocations}
                    className="refresh-button"
                    title="Refresh locations"
                >
                    🔄 Refresh Locations
                </button>
                {lastUpdated && (
                    <div className="last-updated">
                        Last updated: {lastUpdated.toLocaleTimeString()}
                    </div>
                )}
            </div>
            <div
                id="google-map-container"
                style={{ width: "100%", height: "600px" }}
            />
        </div>
    );
};

export default FreightMapComponent;